import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Dialog, InputGroup } from '@blueprintjs/core'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus'
import { faGripVertical } from '@fortawesome/pro-solid-svg-icons/faGripVertical'
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons/faTrashCan'
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark'
import styles from './createActionModal.module.scss'

const INITIAL_DRAFT_ACTION_COUNT = 1

/**
 * Returns the next monotonically increasing temporary id for a draft action.
 *
 * @param {Array<{id: number}>} actions Existing draft actions.
 * @returns {number} Next draft action id.
 */
const getNextDraftActionId = (actions) =>
    actions.reduce((maxId, action) => Math.max(maxId, action.id), 0) + 1

/**
 * Reorders draft actions after drag-and-drop.
 *
 * @param {Array<object>} actions Current ordered actions.
 * @param {number} fromIndex Source index.
 * @param {number} toIndex Destination index.
 * @returns {Array<object>} Reordered actions.
 */
const reorderDraftActions = (actions, fromIndex, toIndex) => {
    const reorderedActions = Array.from(actions)
    const [movedAction] = reorderedActions.splice(fromIndex, 1)
    reorderedActions.splice(toIndex, 0, movedAction)

    return reorderedActions
}

/**
 * Creates an initial draft actions list for the modal.
 *
 * @returns {Array<{id: number, name: string}>} Initial draft actions.
 */
const getInitialDraftActions = () => {
    return Array.from({ length: INITIAL_DRAFT_ACTION_COUNT }, (_, index) => ({
        id: index + 1,
        name: '',
    }))
}

/**
 * Modal for creating one or more template actions before persisting.
 *
 * @param {object} props Component props.
 * @param {boolean} props.isOpen Controls dialog visibility.
 * @param {boolean} props.isSubmitting Disables interactions while saving.
 * @param {Function} props.onClose Closes the modal.
 * @param {Function} props.onSubmit Persists action names.
 * @returns {JSX.Element}
 */
function CreateActionModal({ isOpen, isSubmitting, onClose, onSubmit }) {
    const [draftActions, setDraftActions] = useState(getInitialDraftActions)
    const draftActionsRowsRef = useRef(null)
    const shouldScrollToEndRef = useRef(false)
    const lastInputRef = useRef(null)

    useEffect(() => {
        if (!isOpen) {
            return
        }

        setDraftActions(getInitialDraftActions())
    }, [isOpen])

    useEffect(() => {
        if (!isOpen) {
            return
        }

        requestAnimationFrame(() => {
            lastInputRef.current?.focus()
        })
    }, [isOpen, draftActions.length])

    useEffect(() => {
        if (!shouldScrollToEndRef.current) {
            return
        }

        const rowsElement = draftActionsRowsRef.current

        requestAnimationFrame(() => {
            if (rowsElement) {
                rowsElement.scrollTop = rowsElement.scrollHeight
            }

            lastInputRef.current?.focus()

            shouldScrollToEndRef.current = false
        })
    }, [draftActions.length])

    const hasAtLeastOneDraftAction = useMemo(
        () => draftActions.some((action) => action.name.trim().length > 0),
        [draftActions]
    )

    const handleClose = useCallback(() => {
        if (isSubmitting) {
            return
        }

        onClose()
    }, [isSubmitting, onClose])

    const handleDraftActionNameChange = useCallback((actionId, value) => {
        setDraftActions((previousActions) =>
            previousActions.map((action) =>
                action.id === actionId ? { ...action, name: value } : action
            )
        )
    }, [])

    const handleAddDraftAction = useCallback(() => {
        shouldScrollToEndRef.current = true

        setDraftActions((previousActions) => {
            const nextId = getNextDraftActionId(previousActions)

            return [...previousActions, { id: nextId, name: '' }]
        })
    }, [])

    const handleDeleteDraftAction = useCallback((actionId) => {
        setDraftActions((previousActions) => {
            if (previousActions.length === 1) {
                return [{ ...previousActions[0], name: '' }]
            }

            return previousActions.filter((action) => action.id !== actionId)
        })
    }, [])

    const handleDragEnd = useCallback((result) => {
        const { source, destination } = result

        if (!destination || source.index === destination.index) {
            return
        }

        setDraftActions((previousActions) => {
            return reorderDraftActions(
                previousActions,
                source.index,
                destination.index
            )
        })
    }, [])

    const assignRowsRef = useCallback(
        (node, providedInnerRef) => {
            providedInnerRef(node)
            draftActionsRowsRef.current = node
        },
        [draftActionsRowsRef]
    )

    const handleSubmit = useCallback(async () => {
        const nextNames = draftActions
            .map((action) => action.name.trim())
            .filter(Boolean)

        if (!nextNames.length || isSubmitting) {
            return
        }

        await onSubmit(nextNames)
    }, [draftActions, isSubmitting, onSubmit])

    return (
        <Dialog
            isOpen={isOpen}
            canEscapeKeyClose={!isSubmitting}
            canOutsideClickClose={!isSubmitting}
            className={styles.addActionsDialog}
            onClose={handleClose}
        >
            <div className={styles.addActionsDialogHeader}>
                <h5 className={styles.addActionsDialogTitle}>Add action(s)</h5>
                <button
                    type="button"
                    className={styles.addActionsDialogCloseButton}
                    aria-label="Close add actions modal"
                    onClick={handleClose}
                    disabled={isSubmitting}
                >
                    <FontAwesomeIcon icon={faXmark} />
                </button>
            </div>

            <div className={styles.addActionsDialogBody}>
                <p className={styles.addActionsLabel}>Action name</p>

                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="create-action-modal-actions-list">
                        {(provided) => (
                            <div
                                className={styles.addActionsRows}
                                ref={(node) =>
                                    assignRowsRef(node, provided.innerRef)
                                }
                                {...provided.droppableProps}
                            >
                                {draftActions.map((actionItem, index) => (
                                    <Draggable
                                        key={actionItem.id}
                                        draggableId={String(actionItem.id)}
                                        index={index}
                                        isDragDisabled={isSubmitting}
                                    >
                                        {(dragProvided, dragSnapshot) => (
                                            <div
                                                ref={dragProvided.innerRef}
                                                {...dragProvided.draggableProps}
                                                className={`${
                                                    styles.addActionsRow
                                                } ${
                                                    dragSnapshot.isDragging
                                                        ? styles.addActionsRowDragging
                                                        : ''
                                                }`}
                                            >
                                                <span
                                                    className={
                                                        styles.addActionsDragIcon
                                                    }
                                                    {...dragProvided.dragHandleProps}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faGripVertical}
                                                        aria-hidden
                                                    />
                                                </span>
                                                <InputGroup
                                                    value={actionItem.name}
                                                    placeholder="Enter action name"
                                                    onChange={(event) =>
                                                        handleDraftActionNameChange(
                                                            actionItem.id,
                                                            event.target.value
                                                        )
                                                    }
                                                    className={
                                                        styles.addActionsInput
                                                    }
                                                    disabled={isSubmitting}
                                                    inputRef={
                                                        index ===
                                                        draftActions.length - 1
                                                            ? lastInputRef
                                                            : undefined
                                                    }
                                                />
                                                <button
                                                    type="button"
                                                    className={
                                                        styles.addActionsDeleteButton
                                                    }
                                                    onClick={() =>
                                                        handleDeleteDraftAction(
                                                            actionItem.id
                                                        )
                                                    }
                                                    aria-label={`Remove draft action ${
                                                        actionItem.name ||
                                                        actionItem.id
                                                    }`}
                                                    disabled={isSubmitting}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faTrashCan}
                                                    />
                                                </button>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>

                <button
                    type="button"
                    className={styles.addActionInlineButton}
                    onClick={handleAddDraftAction}
                    disabled={isSubmitting}
                >
                    <FontAwesomeIcon icon={faPlus} />
                    <span>Add action</span>
                </button>
            </div>

            <div className={styles.addActionsDialogFooter}>
                <Button
                    className={styles.cancelAddActionsButton}
                    onClick={handleClose}
                    disabled={isSubmitting}
                >
                    Cancel
                </Button>

                <Button
                    className={styles.confirmAddActionsButton}
                    onClick={handleSubmit}
                    disabled={!hasAtLeastOneDraftAction || isSubmitting}
                >
                    {isSubmitting ? 'Adding...' : 'Add to template'}
                </Button>
            </div>
        </Dialog>
    )
}

CreateActionModal.propTypes = {
    isOpen: PropTypes.bool,
    isSubmitting: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
}

CreateActionModal.defaultProps = {
    isOpen: false,
    isSubmitting: false,
}

export default CreateActionModal
